import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { usePermission } from '../../hooks/usePermission';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { useTranslation } from '../../utils/useTranslation';
import { 
    getProductLaborPriceHistory, 
    createProductLaborPrice, 
    clearProductLaborPriceError 
} from '../../actions/product';

const ProductLaborPriceModal = ({ 
    show, 
    handleClose, 
    product, 
    firmId,
    onSuccess 
}) => {
    const { userCan } = usePermission();
    const { canManagerEditProduct } = useManagerPermissions();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const [formData, setFormData] = useState({
        labor_price_per_unit: '',
        notes: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showResetButton, setShowResetButton] = useState(false);

    // Get current user for permissions
    const auth = useSelector(state => state.auth);
    
    // Get product labor price state from Redux (now in product reducer)
    const productState = useSelector(state => state.product);
    
    // Use Redux state instead of local state with safe destructuring
    const { 
        product_labor_price_history: priceHistory = [], 
        product_labor_price_loading: loadingHistory = false, 
        product_labor_price_error: reduxError = null 
    } = productState || {};

    const fetchPriceHistory = useCallback(() => {
        if (!product?.id || !firmId) return;
        dispatch(getProductLaborPriceHistory(product.id));
    }, [product?.id, firmId, dispatch]);

    useEffect(() => {
        if (product && show) {
            // Fetch labor price history first
            fetchPriceHistory();
        }
    }, [product, show, dispatch, fetchPriceHistory]);
    
    // Update form data when price history is loaded (only on initial load)
    useEffect(() => {
        if (priceHistory && priceHistory.length > 0 && isInitialLoad) {
            // Find the active labor price
            const activePrice = priceHistory.find(price => price.is_active);
            if (activePrice) {
                setFormData(prev => ({
                    ...prev,
                    labor_price_per_unit: activePrice.labor_price_per_unit || '',
                    notes: ''
                }));
            }
            setIsInitialLoad(false);
        }
    }, [priceHistory, isInitialLoad]);

    // Watch for Redux errors and display them
    useEffect(() => {
        if (reduxError) {
            setError(reduxError);
        }
    }, [reduxError]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.labor_price_per_unit || parseFloat(formData.labor_price_per_unit) < 0) {
            setError(t('updateLaborPrice.pleaseEnterValidLaborPrice'));
            return;
        }

        if (!firmId) {
            setError(t('updateLaborPrice.firmIdRequired'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        
        // Clear any previous Redux errors
        dispatch(clearProductLaborPriceError());

        try {
            const result = await dispatch(createProductLaborPrice({
                product_id: product.id,
                firm_id: firmId,
                labor_price_per_unit: parseFloat(formData.labor_price_per_unit),
                currency: 'UZS',
                notes: formData.notes || 'Updated from products page'
            }));

            if (result.success) {
                setSuccess(t('updateLaborPrice.laborPriceUpdatedSuccessfully'));
                
                // Reset form to empty state
                setFormData({
                    labor_price_per_unit: '',
                    notes: ''
                });
                
                // Show reset button for manual form reset
                setShowResetButton(true);
                
                // Refresh price history to show the new record
                fetchPriceHistory();
                
                // Call success callback to refresh data
                if (onSuccess) {
                    onSuccess();
                }
                
                // Clear success message after 3 seconds but keep modal open
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            } else {
                setError(result.message || t('updateLaborPrice.failedToUpdateLaborPrice'));
            }
        } catch (err) {
            setError(t('updateLaborPrice.networkError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setFormData({
            labor_price_per_unit: '',
            notes: ''
        });
        setError('');
        setSuccess('');
        setLoading(false);
        setIsInitialLoad(true);
        setShowResetButton(false);
        handleClose();
    };

    const handleResetForm = () => {
        setFormData({
            labor_price_per_unit: '',
            notes: ''
        });
        setError('');
        setSuccess('');
        setShowResetButton(false);
    };

    if (!product) return null;

    return (
        <Modal show={show} onHide={handleCloseModal} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    {t('updateLaborPrice.title')} - {product.name}
                </Modal.Title>
            </Modal.Header>
            
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" onClose={() => setError('')} dismissible>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                            {success}
                        </Alert>
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>{t('updateLaborPrice.productName')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={product.name}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </div>
                        
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>{t('updateLaborPrice.currentStock')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={`${product.in_stock || 0} ${product.unit?.name || ''}`}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>{t('updateLaborPrice.currentLaborPrice')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={(() => {
                                        if (priceHistory && priceHistory.length > 0) {
                                            const activePrice = priceHistory.find(price => price.is_active);
                                            return activePrice ? `${activePrice.labor_price_per_unit} UZS` : '0 UZS';
                                        }
                                        return '0 UZS';
                                    })()}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </div>
                        
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>{t('updateLaborPrice.firm')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={product.firm?.name || 'N/A'}
                                    disabled
                                    className="bg-light"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <hr />

                    <div className="row">
                        <div className="col-md-12">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    {t('updateLaborPrice.newLaborPricePerUnitRequired')}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="labor_price_per_unit"
                                    value={formData.labor_price_per_unit}
                                    onChange={handleInputChange}
                                    placeholder={t('updateLaborPrice.enterNewLaborPricePlaceholder')}
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <Form.Text className="text-muted">
                                    {t('updateLaborPrice.enterNewLaborPrice')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('updateLaborPrice.notes')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            placeholder={t('updateLaborPrice.notesPlaceholder')}
                            rows="3"
                        />
                    </Form.Group>

                    <hr />
                    
                    {/* Labor Price History Section */}
                    <div className="mb-4">
                        <h6 className="mb-3">
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            {t('updateLaborPrice.laborPriceHistory')}
                        </h6>
                        
                        {loadingHistory ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">{t('updateLaborPrice.loadingPriceHistory')}</span>
                            </div>
                        ) : priceHistory.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t('updateLaborPrice.date')}</th>
                                            <th>{t('updateLaborPrice.price')}</th>
                                            <th>{t('updateLaborPrice.currency')}</th>
                                            <th>{t('updateLaborPrice.status')}</th>
                                            <th>{t('updateLaborPrice.notesColumn')}</th>
                                            <th>{t('updateLaborPrice.updatedBy')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {priceHistory.map((item, index) => (
                                            <tr key={index} className={item.is_active ? 'table-success' : 'table-light'}>
                                                <td>
                                                    {new Date(item.created_at).toLocaleDateString('en-GB')}
                                                    <br />
                                                    <small className="text-muted">
                                                        {new Date(item.created_at).toLocaleTimeString('en-GB', {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </small>
                                                </td>
                                                <td>
                                                    <strong>{parseFloat(item.labor_price_per_unit).toLocaleString('en-US', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}</strong>
                                                </td>
                                                <td>{item.currency}</td>
                                                <td>
                                                    {item.is_active ? (
                                                        <span className="badge bg-success">{t('updateLaborPrice.active')}</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">{t('updateLaborPrice.inactive')}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {item.notes ? (
                                                        <span title={item.notes}>
                                                            {item.notes.length > 30 ? 
                                                                `${item.notes.substring(0, 30)}...` : 
                                                                item.notes
                                                            }
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {item.created_by_user?.name || 'System'}
                                                    </small>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-3 text-muted">
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                {t('updateLaborPrice.noPriceHistory')}
                            </div>
                        )}
                    </div>

                    <div className="alert alert-info">
                        <strong>{t('updateLaborPrice.note')}</strong> {t('updateLaborPrice.noteText')}
                    </div>
                </Modal.Body>
                
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100">
                        <div>
                            {showResetButton && (
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={handleResetForm}
                                    className="me-2"
                                >
                                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                                    {t('updateLaborPrice.resetForm')}
                                </Button>
                            )}
                        </div>
                        
                        <div>
                            <Button 
                                variant="secondary" 
                                onClick={handleCloseModal}
                                disabled={loading}
                                className="me-2"
                            >
                                <FontAwesomeIcon icon={faTimes} className="me-2" />
                                {t('updateLaborPrice.cancel')}
                            </Button>
                            
                            <Button 
                                variant="primary" 
                                type="submit"
                                disabled={loading || !formData.labor_price_per_unit}
                            >
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {t('updateLaborPrice.updating')}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} className="me-2" />
                                        {t('updateLaborPrice.updateLaborPrice')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

ProductLaborPriceModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    product: PropTypes.object,
    firmId: PropTypes.number,
    onSuccess: PropTypes.func
};

export default ProductLaborPriceModal;
